
import { useState, useEffect, useRef } from 'react';

function AnimatedCounter({ target, suffix = '', duration = 2000 }: { target: number; suffix?: string; duration?: number }) {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    let startTime: number;
    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(easeOut * target));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, target, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
}

export default function Stats() {
  const stats = [
    {
      value: 150,
      suffix: '+',
      label: 'Projetos Entregues',
      icon: 'ri-rocket-2-line',
      color: 'from-red-500 to-red-700'
    },
    {
      value: 98,
      suffix: '%',
      label: 'Clientes Satisfeitos',
      icon: 'ri-heart-pulse-line',
      color: 'from-cyan-500 to-cyan-700'
    },
    {
      value: 10,
      suffix: '+',
      label: 'Anos de Experiência',
      icon: 'ri-award-line',
      color: 'from-emerald-500 to-emerald-700'
    },
    {
      value: 50,
      suffix: 'M+',
      label: 'Dados Processados',
      icon: 'ri-database-2-line',
      color: 'from-amber-500 to-amber-700'
    }
  ];

  return (
    <section className="py-16 sm:py-20 bg-dark-900 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-red-600/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-cyan-600/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        <div className="text-center mb-12 sm:mb-16">
          <div className="flex items-center justify-center mb-4">
            <div className="w-3 h-3 rounded-full bg-red-500 mr-3 animate-pulse"></div>
            <span className="text-sm font-semibold text-gray-500 tracking-wider uppercase">NÚMEROS QUE IMPRESSIONAM</span>
            <div className="w-3 h-3 rounded-full bg-red-500 ml-3 animate-pulse"></div>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Resultados <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-red-600">Comprovados</span>
          </h2>
          <p className="text-base sm:text-lg text-gray-400 max-w-2xl mx-auto">
            Números que refletem dedicação, qualidade e compromisso com a excelência
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {stats.map((stat, index) => (
            <div 
              key={index}
              className="group relative bg-dark-800/80 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-dark-600 hover:border-red-600/50 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-red-600/10"
            >
              {/* Glow Effect */}
              <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-10 rounded-2xl transition-opacity duration-500`}></div>
              
              {/* Icon */}
              <div className={`w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center mb-4 sm:mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                <i className={`${stat.icon} text-xl sm:text-2xl text-white`}></i>
              </div>

              {/* Number */}
              <div className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-2 tracking-tight">
                <AnimatedCounter target={stat.value} suffix={stat.suffix} />
              </div>

              {/* Label */}
              <p className="text-sm sm:text-base text-gray-400 font-medium">{stat.label}</p>

              {/* Decorative Line */}
              <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${stat.color} rounded-b-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
            </div>
          ))}
        </div>

        {/* Trust Badge */}
        <div className="mt-12 sm:mt-16 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
          <div className="flex items-center gap-2 text-gray-400">
            <i className="ri-shield-check-line text-emerald-500 text-xl"></i>
            <span className="text-sm">Dados Verificados</span>
          </div>
          <div className="flex items-center gap-2 text-gray-400">
            <i className="ri-time-line text-cyan-500 text-xl"></i>
            <span className="text-sm">Atualizado em 2025</span>
          </div>
          <div className="flex items-center gap-2 text-gray-400">
            <i className="ri-global-line text-red-500 text-xl"></i>
            <span className="text-sm">Atuação Nacional</span>
          </div>
        </div>
      </div>
    </section>
  );
}
